import React, {useCallback, useState} from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import {statusPageAPI} from 'features/statusPage/api/statusPageAPI';
import {ExceededModal, IUserOutput, useActiveToggle} from 'entities/user';
import OpenLinkNewWindow from 'shared/ui/IconButton/OpenLinkNewWindow/OpenLinkNewWindow';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import YesNo from 'shared/ui/YesNo/YesNo';
import MoreActionsItem from 'shared/ui/MoreActions/MoreActionsItem';
import EntityActionsMenu from 'shared/ui/MoreActions/EntityActionsMenu';
import Copying from 'shared/ui/icons/Copying';
import IStatusPage from 'features/statusPage/model/StatusPage/IStatusPage';
import {useCopyToClipboard} from 'shared/lib/hooks/useCopyToClipboard';
import {getFormattedDate} from 'shared/lib/datetime';
import {isTranslate} from 'shared/lib/lang';

import {PALETTE} from 'shared/config/model/palette';

import StatusPageDeleteModal from '../StatusPageDeleteModal/StatusPageDeleteModal';
import StatusPageModal from '../StatusPageModal/StatusPageModal';

import styles from './StatusPageRow.module.scss';

interface IStatusPageRowProps {
    item: IStatusPage,
    user: IUserOutput | null,
    canCreate: boolean,
}

type ModalType = 'delete' | 'limit' | 'edit' | null;

const StatusPageRow: React.FC<IStatusPageRowProps> = ({item, user, canCreate}) => {
    const [activeModal, setActiveModal] = useState<ModalType>(null);
    const {copy} = useCopyToClipboard();
    const [updateStatusPage] = statusPageAPI.useUpdateStatusPageMutation();

    const handleCloseModal = () => setActiveModal(null);

    const {isActive, toggle} = useActiveToggle({
        initial: item.is_active,
        update: useCallback(
            (next: boolean) => updateStatusPage({uuid: item.uuid, is_active: next}),
            [updateStatusPage, item.uuid],
        ),
        activatedText: t`Страница активирована`,
        deactivatedText: t`Страница деактивирована`,
        canActivate: canCreate,
        onLimitExceeded: useCallback(() => setActiveModal('limit'), []),
    });

    return (
        <>
            <tr className={cn(styles.row, !isActive && styles.inactive)}>
                <td className={styles.nameCell}>
                    <span className={styles.name}>{item.name}</span>
                </td>
                <td className={styles.urlCell}>
                    <OpenLinkNewWindow url={item.url}/>
                </td>
                <td className={styles.monitorsCell}>{item.monitors_count}</td>
                <td>
                    <YesNo
                        condition={item.hide_banner && !!user?.tariff.status_page_hide_banner_enabled}
                        color={PALETTE.success}
                    />
                </td>
                <td className={styles.dateCell}>
                    {getFormattedDate(item.created_at, {
                        format: !isTranslate() ? 'DD.MM.YYYY' : 'YYYY-MM-DD',
                        tzString: user?.timezone,
                    })}
                </td>
                <td className={styles.statusCell}>
                    <StatusToggle
                        condition={isActive}
                        onClick={toggle}
                        preventToggle
                        textActive={t`Активна`}
                        notActiveText={t`Неактивна`}
                    />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex justify-content-center">
                        <EntityActionsMenu
                            placement="auto"
                            onEdit={() => setActiveModal('edit')}
                            onDelete={() => setActiveModal('delete')}
                            extraActions={[
                                <MoreActionsItem
                                    key="copy"
                                    label={t`Скопировать ссылку`}
                                    icon=<Copying/>
                                    onClick={() => copy(item.url)}
                                />,
                            ]}
                        />
                    </div>
                </td>
            </tr>

            <StatusPageDeleteModal
                item={item}
                open={activeModal === 'delete'}
                handleClose={handleCloseModal}
            />

            <StatusPageModal
                item={item}
                open={activeModal === 'edit'}
                handleClose={handleCloseModal}
            />

            <ExceededModal
                isOpen={activeModal === 'limit'}
                handleClose={handleCloseModal}
                type="status_pages"
            />
        </>
    );
};

export default StatusPageRow;
